<template>
  <div class="uploadSection max-w-[600px]">
    <div
      v-show="false"
      class="js-manuallyUpload"
      @click="() => { 
        upload()
      }"
    ></div>
    
    <div>
      <div
        v-if="errors && errors.length"
        class="infoMsg warning"
      >
        <ul>
          <template 
            v-for="error, i in errors"
          >
            <li
              v-if="error"
              :key="i"
              v-html="error"
            ></li>
          </template>
        </ul>
      </div>

      <template v-if="props.additionalOptions?.externalUpload">
        <h3 class="text-lg text-center font-medium text-neutral-800">
          {{ $t('scanQRCodeToUpload') }}
        </h3>

        <div class="rounded flex justify-center">
          <a
            v-if="qrcode"
            :href="generatedLink"
            target="_blank"
          >
            <img
              :src="qrcode.value"
            />
          </a>
        </div>

        <h3 class="text-lg text-center font-medium text-neutral-800 mt-2 mb-6">
          {{ $t('or') }}
        </h3>
      </template>

      <h3
        v-if="props.additionalOptions?.isMobile && props.additionalOptions?.titleFieldMobileLabel"
        class="text-lg text-center font-medium text-neutral-800"
      >
        {{ props.additionalOptions.titleFieldMobileLabel }}
      </h3>
    </div>
  
    <div
      class="bg-[#F7F7F7] rounded p-4"
    >
      <input
        ref="fileInput"
        type="file"
        name="file"
        :accept="props.accept"
        :disabled="props.disabled"
        class="hidden"
        @change="handleFileChange"
      />
      
      <Button
        :label="props.additionalOptions?.titleBtn ? props.additionalOptions.titleBtn : $t('uploadFile')"
        severity="secondary"
        icon="icon icon-cloud-upload"
        class="w-full"
        :disabled="props.disabled || isProcess"
        @click="fileInput.click()"
      />

      <div
        class="text-xs text-center text-neutral-600 font-extralight leading-5 pt-4"
      >
        <div
          v-if="isProcess"
          class="mt-4"
        >
          <div
            class="progress"
          >
            <div class="indeterminate"></div>
          </div>
          <p
            v-if="props.disclaimer"
            class="mt-4"
            v-html="props.disclaimer"
          >
          </p>
        </div>

        <div
          v-else-if="fileName && !isProcess"
          class="flex items-center justify-between"
        >
          <div
            v-if="fileName"
            class="font-light flex-auto text-left"
          >
            {{ fileName }}
          </div>

          <div class="flex items-center">
            <div
              v-if="fileSize"
              class="mr-4 w-[100px]"
            >
              {{ fileSize }}
            </div>

            <Button
              :label="null"
              icon="icon icon-trash"
              size="small"
              variant="outlined"
              severity="secondary"
              :pt-options="{
                variant: 'extra'
              }"
              @click="remove()"
            />
          </div>
        </div>

        <div
          v-else
          class="text"
          v-html="props.placeholder ? props.placeholder : $t('uploadFile')"
        ></div>
      </div>
    </div>

    <div
      v-if="props.additionalOptions?.showTitleField && props.additionalOptions?.manuallyUpload && !isProcess"
      class="pt-6"
    >
      <div class="input-field mb-0">
        <label
          class="active left-0"
        >{{ props.additionalOptions.titleFieldLabel }}</label>
        
        <InputText
          v-model="fileTitle"
          :placeholder="props.additionalOptions.titleFieldPlaceholder"
          :disabled="props.disabled || isProcess"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { nextTick } from 'vue'

const { $i18n: i18n, $websocket } = useNuxtApp()
const { uploadFile, deleteFile, uploadVimeo } = useGeneral()
const { memberToken } = useApi()
const { confGlobal } = useConfig()
const { useRoomId } = useCustomTools()

const fileInput = ref(null)
const fileName = ref(null)
const fileUrl = ref(null)
const fileSize = ref(null)
const isProcess = ref(false)
const errors = ref(false)
const newFile = ref(null)
const currentUploadType = ref(false) // Store uploadType locally

const fileTitle = ref(null)

/**
* Emits
*/
const emit = defineEmits(['returnData', 'submitForm', 'file', 'isPending', 'update:uploadType'])

/**
* Props
*/
const props = defineProps({
  uuid: {
    type: [String],
    default: ''
  },
  label: {
    type: [String, Boolean],
    default: ''
  },
  name: {
    type: [String, Boolean],
    default: ''
  },
  fileFieldName: {
    type: [String, Boolean],
    default: ''
  },
  attachFile: {
    type: [String, Array, Number, Object],
    default: ''
  },
  elementClasses: {
    type: [String],
    default: ''
  },
  placeholder: {
    type: [String],
    default: ''
  },
  disclaimer: {
    type: [String],
    default: ''
  },
  accept: {
    type: [String],
    default: ''
  },
  disabled: {
    type: [Boolean],
    default: false
  },
  additionalOptions: {
    type: [Object],
    default: null
  },
  max: {
    type: [Number],
    default: null
  }
})

/**
* Connect to WebSocket
*/
const roomId = props.uuid ? props.uuid : useRoomId().value
const generatedLink = ref(`${confGlobal.currentLocationUrl}/shared/uploader?roomId=${roomId}`)

/**
* Async QR
*/
const qrcode = ref(false)
const useQRCode = ref()
onNuxtReady(async () => {
  const module = await import('@vueuse/integrations/useQRCode')
  useQRCode.value = markRaw(module)
  qrcode.value = useQRCode.value.useQRCode(generatedLink.value)
})

/**
* Connect to WebSocket
*/
const { data, send } = $websocket

/*
* Mounted Component
* Open Room
*/
onMounted(() => {
  send(JSON.stringify({
    type: 'joinRoom',
    status: 'success',
    roomId,
    timestamp: new Date().getTime()
  }))
})

/*
* Unmounted Component
* Close room
*/
onUnmounted(() => { 
  send(JSON.stringify({
    type: 'close',
    status: 'success',
    roomId,
    timestamp: new Date().getTime()
  }))
})

/**
* Get Updated Data from WebSocket
*/
watch(data, (newData) => {
  const parsedData = JSON.parse(newData)

  emit('isPending', false)

  if (parsedData.type === 'requestParams') {
    /*
    * After connecting to the second page, we send the parameters for forming the form
    */
    send(JSON.stringify({
      type: 'updateParams',
      status: 'success',
      roomId,
      data: props,
      memberToken: memberToken.value,
      timestamp: new Date().getTime()
    }))

    isProcess.value = false
  } else if (parsedData.type === 'update') {
    if (parsedData.status === 'success') {
      emit('returnData', parsedData.data)
    
      isProcess.value = false
    } else if (parsedData.status === 'pending') {
      emit('isPending', true)
        
      isProcess.value = true
    } else if (parsedData?.status === 'error') {
      isProcess.value = false
    }
  } 
}, { deep: true })

/**
* Format File Size
*/
function formatFileSize(bytes) {
  const kilobyte = 1024
  const megabyte = kilobyte * 1024

  if (bytes >= megabyte) {
    return (bytes / megabyte).toFixed(2) + ' MB'
  } else if (bytes >= kilobyte) {
    return (bytes / kilobyte).toFixed(2) + ' KB'
  } else {
    return bytes + ' B'
  }
}

/**
* File Details
*/
const fileDetails = () => { 
  fileName.value = newFile.value.name
  if (fileName.value.length > 20) {
    fileName.value = fileName.value.substring(0, 10) + '...' + fileName.value.substring(fileName.value.length - 7, fileName.value.length)
  }
  fileSize.value = formatFileSize(newFile.value.size)
}

/**
* Upload File
*/
const upload = async () => {
  errors.value = []

  if (!newFile.value) {
    errors.value.push(i18n?.t('uploadingErrorFile'))

    return false
  }

  let emitReturn = false

  isProcess.value = true

  const formData = new FormData()

  if (props.max) {
    if (newFile.value.size > props.max) { 
      emitReturn = {
        error: i18n?.t('uploadingSizeFailed', { maxSize: formatFileSize(props.max) })
      }
    }
  }

  /**
  * Uploading
  */
  if (!emitReturn) {
    // Use local uploadType if available, fallback to props
    const uploadType = currentUploadType.value || props.additionalOptions?.uploadType
    
    send(JSON.stringify({
      type: 'close',
      status: 'success',
      roomId,
      timestamp: new Date().getTime()
    }))
    
    if (uploadType === 'vimeo' || uploadType === 'vimeoThankYou') {
      formData.append('title', fileTitle.value ? fileTitle.value : newFile.value.name)
      formData.append('video', newFile.value)
      if (props.additionalOptions?.uuid) {
        formData.append('uuid', props.additionalOptions.uuid)
      }

      emitReturn = uploadVimeo(
        formData,
        uploadType === 'vimeoThankYou' ? `/order/thank-you/${props.additionalOptions?.uuid}/upload-video` : undefined
      ).then((res) => {
        isProcess.value = false

        if (res?.data?.video_info) {  
          return {
            data: {
              video_info: res.data.video_info
            }
          }
        } else {
          if (res?.description[0]) {
            errors.value.push(res?.description[0])
          } else { 
            errors.value.push(i18n?.t('uploadingErrorFile'))
          }
  
          return false
        }
      }).catch(() => {
        isProcess.value = false

        errors.value.push(i18n?.t('uploadingErrorFile'))

        return false
      })
    } else {
      formData.append('file', newFile.value)

      emitReturn = uploadFile(formData).then((res) => {
        fileUrl.value = res.data?.main?.url

        isProcess.value = false
        if (res?.data?.main) {
          return {
            data: res.data?.main?.url
          }
        } else {
          errors.value.push(res.error)
  
          return {
            error: res
          }
        }
      }).catch(() => {
        isProcess.value = false

        errors.value.push(i18n?.t('uploadingErrorFile'))

        return {
          data: ''
        }
      })
    }
  }

  /**
  * Return / Emit
  */
  emit('isPending', true)

  const returnData = await emitReturn
  if (returnData.error) {
    errors.value.push(returnData.error)
  }
  isProcess.value = false

  emit('isPending', false)

  if (!props.additionalOptions?.manuallyUpload) {
    emit('file', returnData ? returnData : false)
  }

  emit('returnData', returnData ? returnData : false)
}

/**
* Remove Upload File
*/
const remove = () => {
  if (fileUrl.value) {
    const fileNameArray = fileUrl.value.split('/')
    const s3folder = fileNameArray[fileNameArray.length - 2]
    const fileKey = fileNameArray[fileNameArray.length - 1]

    deleteFile({
      s3folder,
      fileName: fileKey,
      uploadType: props.additionalOptions?.uploadType 
    }).then(() => {
      emit('file', '')
    }).catch(() => {
      emit('file', '')
    })
  }

  fileTitle.value = ''
  newFile.value = ''
  fileName.value = ''
  fileSize.value = ''
  fileUrl.value = ''

  emit('returnData', '')
}

/**
* Init
*/
onMounted(() => {
  if (props.attachFile?.length) {
    fileUrl.value = props.attachFile
    if (fileUrl.value) {
      const fileNameTmp = fileUrl.value.split('/')[fileUrl.value.split('/').length - 1]
      fileName.value = fileNameTmp.substring(0, 10) + '...' + fileNameTmp.substring(fileNameTmp.length - 7, fileNameTmp.length)
    }
  }
})

/**
* Handle File Change
*/
const handleFileChange = async (e) => {
  newFile.value = e.target.files[0]
  
  if (!newFile.value) return
  
  fileDetails()
  
  // Set uploadType immediately when file is selected, before any processing
  if (props.additionalOptions?.fromThankYou) {
    let type = ''
    if (newFile.value.type && newFile.value.type.startsWith('video/')) {
      type = 'vimeoThankYou'
    } else if (newFile.value.type && newFile.value.type.startsWith('image/')) {
      type = 'image'
    } else {
      // Fallback to extension check
      const extension = newFile.value.name.split('.').pop().toLowerCase()
      const videoExtensions = ['mp4', 'mov', 'avi', 'quicktime']
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif']
      
      if (videoExtensions.includes(extension)) {
        type = 'vimeoThankYou'
      } else if (imageExtensions.includes(extension)) {
        type = 'image'
      }
    }
    
    // Store the uploadType locally
    currentUploadType.value = type
    
    emit('update:uploadType', type)
    
    // Wait for uploadType to be updated in parent before proceeding
    await nextTick()
    
    // Wait a bit longer to ensure the parent component has updated the schema
    await new Promise(resolve => setTimeout(resolve, 50))
    
    if (!props.additionalOptions?.manuallyUpload) {
      // emit('submitForm', { pending: true }) // BUG: This was causing issues with the form submission
      upload()
    }
  } else {
    if (props.additionalOptions?.manuallyUpload) {
      if (!fileTitle.value && newFile.value.name) { 
        fileTitle.value = newFile.value.name.split('.').slice(0, -1).join('.')
      }
    } else {
      upload()
      // emit('submitForm', { pending: true }) // BUG: This was causing issues with the form submission
    }
  }
}
</script>

<style lang="scss" scoped>
.upload-panel {
  display: flex;
  align-items: center;
  padding: 1rem;
  margin: 0;
  margin-top: 5px;
  background-color: color("grey", "lighten-6");
  border-radius: 5px;
  &__btn {
    flex: 1 auto;
    width: 100%;
    max-width: 160px;
    margin-right: 15px;
    margin-bottom: 0;
    label {
      width: 100%!important;
    }
    .btn {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      min-width: 100px !important;
      padding: 8px 20px;
      line-height: 1rem;
    }
  }
  &__text {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex: 1 1 auto;
    overflow: hidden;
    min-width: 140px;
    font-size: 12px;
    line-height: 16px;
    text-overflow: ellipsis;
    white-space: inherit;
    opacity: .7;
    .right {
      display: flex;
      align-items: center;
    }
    .trashcan {
      cursor: pointer;
    }
  }
}
</style>
